import type { Frame } from "../renderer/Frame";
import { createDrawing, DPoint, DrawingObject, DrawingType, TOOLS, ToolId, isPositionTool } from "./types";
import { hitTest } from "./hitTest";
import { snapPoint, SnapResult } from "./Snap";

interface Pending { type: DrawingType; points: DPoint[]; cursor: DPoint; meta?: Record<string, unknown> }
interface Drag { id: string; handle: number; startX: number; startY: number; orig: DrawingObject; moved: boolean }

export interface ControllerHost {
  list(): DrawingObject[];
  symbol(): string;
  snapEnabled(): boolean;
  onComplete(d: DrawingObject): void;
  onChange(list: DrawingObject[]): void;
  onSelect(id: string | null): void;
}

export const CURSOR_TOOLS = new Set<ToolId>(["select", "cross", "dot", "eraser"]);
export const isDrawingTool = (t: ToolId): t is DrawingType => !CURSOR_TOOLS.has(t);
const PRICE_ONLY = new Set<DrawingType>(["hline", "hlbarrier", "touchbarrier"]);
const TIME_ONLY = new Set<DrawingType>(["vline", "expirymark"]);

// Object engine for chart drawings: placement (multi-click, variable-point, freehand), selection, move/resize with snapping.
export class DrawingsController {
  pending: Pending | null = null;
  quick = false; // Quick Price Range Measurement (dbl-click/dbl-tap origin → drag or click to finish) — same "measure" object as the toolbar tool
  selectedId: string | null = null;
  snap: SnapResult | null = null;
  toolMeta: Record<string, unknown> | null = null;
  private drag: Drag | null = null;
  private dragObj: DrawingObject | null = null;

  constructor(private host: ControllerHost) {}

  select(id: string | null) {
    if (this.selectedId === id) return;
    this.selectedId = id;
    this.host.onSelect(id);
  }

  cancel() { this.pending = null; this.quick = false; this.drag = null; this.dragObj = null; this.snap = null; this.select(null); }

  startQuickMeasure(f: Frame, x: number, y: number) {
    const pt = snapPoint(f, x, y, this.host.snapEnabled()).point;
    this.pending = { type: "measure", points: [pt], cursor: pt };
    this.quick = true;
    this.drag = null; this.dragObj = null;
    this.select(null);
  }

  private placeQuick(f: Frame, x: number, y: number) {
    const p = this.pending!;
    const pt = snapPoint(f, x, y, this.host.snapEnabled()).point;
    this.quick = false;
    if (pt.time === p.points[0].time && pt.price === p.points[0].price) { this.pending = null; return; }
    this.complete("measure", [p.points[0], pt]);
  }

  // Objects to paint this frame (live drag override applied).
  renderList(): DrawingObject[] {
    const list = this.host.list();
    return this.dragObj ? list.map((d) => (d.id === this.dragObj!.id ? this.dragObj! : d)) : list;
  }

  pendingObject(): DrawingObject | null {
    if (!this.pending) return null;
    const spec = TOOLS[this.pending.type];
    const pts = spec.freehand ? this.pending.points : [...this.pending.points, this.pending.cursor];
    return createDrawing(this.pending.type, this.host.symbol(), pts, { id: "__pending", meta: this.pending.meta });
  }

  private complete(type: DrawingType, points: DPoint[], meta?: Record<string, unknown>) {
    this.pending = null;
    this.host.onComplete(createDrawing(type, this.host.symbol(), points, meta ? { meta } : {}));
  }

  // Variable-point tools (polyline/path): double-click or Enter finishes with the points placed so far.
  finish() {
    const p = this.pending;
    if (!p) return;
    const pts = p.points.filter((pt, i) => i === 0 || pt.time !== p.points[i - 1].time || pt.price !== p.points[i - 1].price);
    if (pts.length >= TOOLS[p.type].points) this.complete(p.type, pts, p.meta);
    else this.pending = null;
  }

  // Returns true when the event was consumed (caller must not pan).
  onDown(x: number, y: number, f: Frame, tool: ToolId): boolean {
    if (this.pending && this.quick) { this.placeQuick(f, x, y); return true; }
    if (tool === "eraser") {
      const hit = hitTest(f, this.host.list(), x, y);
      if (hit) this.host.onChange(this.host.list().filter((d) => d.id !== hit.id));
      return true;
    }
    if (isDrawingTool(tool)) {
      const spec = TOOLS[tool];
      const pt = snapPoint(f, x, y, this.host.snapEnabled()).point;
      const meta = { ...(this.toolMeta ?? {}) } as Record<string, unknown>;
      if (tool === "anchoredtext") { meta.ax = (x - f.L.plotLeft) / f.L.plotW; meta.ay = (y - f.L.plotTop) / f.L.priceH; }
      if (!this.pending) this.pending = { type: tool, points: [pt], cursor: pt, meta: Object.keys(meta).length ? meta : undefined };
      else this.pending.points.push(pt);
      if (spec.freehand || spec.variable) return true;
      if (this.pending.points.length >= spec.points) this.complete(tool, this.pending.points, this.pending.meta);
      return true;
    }
    const hit = hitTest(f, this.host.list(), x, y);
    if (!hit) { this.select(null); return false; }
    this.select(hit.id);
    const obj = this.host.list().find((d) => d.id === hit.id)!;
    if (!obj.locked) this.drag = { id: hit.id, handle: hit.handle, startX: x, startY: y, orig: obj, moved: false };
    return true;
  }

  onMove(x: number, y: number, f: Frame, tool: ToolId, mouseDown = false) {
    const wantSnap = (isDrawingTool(tool) && !TOOLS[tool].freehand) || (this.pending != null && this.quick) || (this.drag != null && this.drag.handle >= 0);
    const s = snapPoint(f, x, y, this.host.snapEnabled() && wantSnap);
    this.snap = wantSnap ? s : null;
    if (this.pending) {
      this.pending.cursor = s.point;
      if (TOOLS[this.pending.type].freehand && mouseDown) {
        const last = this.pending.points[this.pending.points.length - 1];
        if (Math.hypot(f.xt(last.time) - x, f.y(last.price) - y) > 2) this.pending.points.push({ time: f.timeAt(x), price: f.priceAt(y) });
      }
    }
    if (!this.drag) return;
    const { orig, handle, startX, startY } = this.drag;
    let pts: DPoint[];
    if (handle === -1) {
      const dx = x - startX, dy = y - startY;
      if (orig.type === "anchoredtext" && orig.meta) {
        this.drag.moved = true;
        this.dragObj = { ...orig, meta: { ...orig.meta, ax: Number(orig.meta.ax) + dx / f.L.plotW, ay: Number(orig.meta.ay) + dy / f.L.priceH } };
        return;
      }
      pts = orig.points.map((p) => ({ time: f.timeAt(f.xt(p.time) + dx), price: f.priceAt(f.y(p.price) + dy) }));
    } else {
      pts = orig.points.map((p) => ({ ...p }));
      const np = s.point;
      if (PRICE_ONLY.has(orig.type)) pts[0] = { ...pts[0], price: np.price };
      else if (TIME_ONLY.has(orig.type)) pts[0] = { ...pts[0], time: np.time };
      else if (isPositionTool(orig.type)) {
        if (handle === 0) pts[0] = np;
        else if (handle === 1) { pts[1] = np; pts[2] = { ...pts[2], time: np.time }; }
        else pts[2] = { ...pts[2], price: np.price };
      } else pts[handle] = np;
    }
    this.drag.moved = true;
    this.dragObj = { ...orig, points: pts };
  }

  onUp() {
    if (this.pending && TOOLS[this.pending.type].freehand) {
      if (this.pending.points.length >= 2) this.complete(this.pending.type, this.pending.points, this.pending.meta);
      else this.pending = null;
    }
    if (this.drag?.moved && this.dragObj) {
      const upd = this.dragObj;
      this.host.onChange(this.host.list().map((d) => (d.id === upd.id ? upd : d)));
    }
    this.drag = null; this.dragObj = null;
  }

  hitAt(x: number, y: number, f: Frame): string | null { return hitTest(f, this.host.list(), x, y)?.id ?? null; }

  get dragging() { return this.drag != null; }
}
